import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { Check, Loader2 } from 'lucide-react';

const templates = [
  { id: 'blank', name: 'Blank Instance', desc: 'Start from scratch' },
  { id: 'personal', name: 'Personal Assistant', desc: 'Pre-configured for productivity' },
  { id: 'support', name: 'Customer Support', desc: 'Optimized for support workflows' },
  { id: 'dev', name: 'Dev Assistant', desc: 'Code review, CI/CD, monitoring' },
];

const hwTiers = [
  { id: 'starter', name: 'Starter', cpu: '2 vCPU', ram: '4GB RAM', disk: '50GB SSD', price: 12 },
  { id: 'professional', name: 'Professional', cpu: '4 vCPU', ram: '8GB RAM', disk: '100GB SSD', price: 29 },
  { id: 'enterprise', name: 'Enterprise', cpu: '8 vCPU', ram: '16GB RAM', disk: '200GB SSD', price: 69 },
];

const regions = [
  { id: 'us-east-1', name: 'US East', flag: 'US' },
  { id: 'us-west-2', name: 'US West', flag: 'US' },
  { id: 'eu-west-1', name: 'EU West', flag: 'EU' },
  { id: 'eu-central-1', name: 'EU Central', flag: 'EU' },
  { id: 'ap-south-1', name: 'Asia South', flag: 'AS' },
  { id: 'ap-northeast-1', name: 'Asia NE', flag: 'AS' },
];

const models = [
  { id: 'claude', name: 'Claude Opus 4.5' },
  { id: 'gpt', name: 'GPT-5.2' },
  { id: 'gemini', name: 'Gemini 3 Flash' },
];

const channels = [
  { id: 'telegram', name: 'Telegram' },
  { id: 'discord', name: 'Discord' },
  { id: 'slack', name: 'Slack' },
];

export default function CreateInstance() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ template: 'blank', tier: 'professional', region: 'us-east-1', model: 'claude', channel: 'telegram', name: '' });
  const [deploying, setDeploying] = useState(false);
  const navigate = useNavigate();

  const deploy = async () => {
    setDeploying(true);
    await api.createInstance(form);
    setTimeout(() => navigate('/dashboard/instances'), 2000);
  };

  const selectedTier = hwTiers.find(t => t.id === form.tier);

  return (
    <div data-testid="create-instance-page" className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-white font-mono">Create Instance</h1>

      {/* Steps indicator */}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(s => (
          <div key={s} className={`flex-1 h-1 rounded-full transition-all ${s <= step ? 'bg-emerald-500' : 'bg-zinc-800'}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-zinc-300">1. Choose Template</h2>
          <div className="grid grid-cols-2 gap-3">
            {templates.map(t => (
              <button key={t.id} data-testid={`template-${t.id}`} onClick={() => setForm({ ...form, template: t.id })} className={`text-left p-5 border rounded-sm transition-all ${form.template === t.id ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#0A0A0A] border-zinc-800 hover:border-zinc-700'}`}>
                <h3 className="text-sm font-mono text-white font-bold mb-1">{t.name}</h3>
                <p className="text-xs text-zinc-600">{t.desc}</p>
              </button>
            ))}
          </div>
          <button data-testid="create-next-1" onClick={() => setStep(2)} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale">Next</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-zinc-300">2. Hardware Tier</h2>
          <div className="grid grid-cols-3 gap-3">
            {hwTiers.map(t => (
              <button key={t.id} data-testid={`hw-${t.id}`} onClick={() => setForm({ ...form, tier: t.id })} className={`text-left p-5 border rounded-sm transition-all ${form.tier === t.id ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#0A0A0A] border-zinc-800 hover:border-zinc-700'}`}>
                <h3 className="text-sm font-mono text-white font-bold mb-2">{t.name}</h3>
                <div className="space-y-1 text-xs text-zinc-500 font-mono">
                  <p>{t.cpu}</p><p>{t.ram}</p><p>{t.disk}</p>
                </div>
                <p className="text-emerald-400 font-mono font-bold mt-3">${t.price}/mo</p>
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-6 py-3 hover:border-zinc-700 transition-all">Back</button>
            <button data-testid="create-next-2" onClick={() => setStep(3)} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale">Next</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-zinc-300">3. Region</h2>
          <div className="grid grid-cols-3 gap-3">
            {regions.map(r => (
              <button key={r.id} data-testid={`region-${r.id}`} onClick={() => setForm({ ...form, region: r.id })} className={`p-4 border rounded-sm text-center transition-all ${form.region === r.id ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#0A0A0A] border-zinc-800 hover:border-zinc-700'}`}>
                <span className="text-lg mb-1 block">{r.flag}</span>
                <p className="text-xs font-mono text-zinc-400">{r.name}</p>
                <p className="text-xs font-mono text-zinc-600">{r.id}</p>
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setStep(2)} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-6 py-3 hover:border-zinc-700 transition-all">Back</button>
            <button data-testid="create-next-3" onClick={() => setStep(4)} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale">Next</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-zinc-300">4. Configure OpenClaw</h2>
          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-1.5">Instance Name</label>
            <input data-testid="create-name-input" type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="my-assistant" />
          </div>
          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-2">AI Model</label>
            <div className="flex gap-3">
              {models.map(m => (
                <button key={m.id} data-testid={`model-${m.id}`} onClick={() => setForm({ ...form, model: m.id })} className={`px-4 py-2.5 rounded-sm font-mono text-sm transition-all ${form.model === m.id ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'border border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
                  {m.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono text-zinc-500 mb-2">Messaging Channel</label>
            <div className="flex gap-3">
              {channels.map(c => (
                <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setForm({ ...form, channel: c.id })} className={`px-4 py-2.5 rounded-sm font-mono text-sm transition-all ${form.channel === c.id ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'border border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setStep(3)} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-6 py-3 hover:border-zinc-700 transition-all">Back</button>
            <button data-testid="create-next-4" onClick={() => setStep(5)} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale">Review</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-zinc-300">5. Review & Deploy</h2>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 space-y-3">
            {[
              ['Template', form.template],
              ['Tier', `${selectedTier?.name} (${selectedTier?.cpu}, ${selectedTier?.ram})`],
              ['Region', form.region],
              ['Name', form.name || 'unnamed'],
              ['Model', models.find(m => m.id === form.model)?.name],
              ['Channel', form.channel],
              ['Est. Cost', `$${selectedTier?.price}/mo`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="font-mono text-zinc-500">{k}</span>
                <span className="font-mono text-white">{v}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setStep(4)} className="border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm px-6 py-3 hover:border-zinc-700 transition-all">Back</button>
            <button data-testid="create-deploy-btn" onClick={deploy} disabled={deploying} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-8 py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50 flex items-center gap-2">
              {deploying ? <><Loader2 size={16} className="animate-spin" /> Deploying...</> : <><Check size={16} /> Deploy Instance</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
